import React from 'react';
import { Plus, Search, X, Users, MessageSquare, Send, Trash2 } from 'lucide-react';
import { Avatar } from '../Profile/Avatar';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { subscribeToConversationUpdates } from '../../lib/chat';
import { Badge } from '../ui/badge';
import { BottomSheet } from '../ui/BottomSheet';

interface Conversation {
  id: string;
  type: 'direct' | 'group';
  otherUser?: {
    id: string;
    username: string;
    avatar_url?: string;
    isOnline?: boolean;
  };
  name?: string;
  image_url?: string;
  participants?: Array<{
    id: string;
    username: string;
    avatar_url?: string;
  }>;
  admin_id?: string;
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount?: number;
}

interface ConversationListProps {
  conversations: Conversation[];
  activeConversationId?: string | null;
  onSelect: (conversation: Conversation) => void;
  onConversationUpdate: (conversations: Conversation[]) => void;
  onCreateGroup?: () => void;
  onNewMessage?: () => void;
  triggerNewMessage?: boolean;
  onNewMessageHandled?: () => void;
}

function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: 'short' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function ConversationList({ 
  conversations, 
  activeConversationId,
  onSelect,
  onConversationUpdate,
  onCreateGroup,
  onNewMessage,
  triggerNewMessage,
  onNewMessageHandled
}: ConversationListProps) {
  const navigate = useNavigate();
  const [showNewConversation, setShowNewConversation] = React.useState(false);
  const [showNewMessageMenu, setShowNewMessageMenu] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [localConversations, setLocalConversations] = React.useState<Conversation[]>(conversations);
  const [selectedConversation, setSelectedConversation] = React.useState<Conversation | null>(null);
  const [showDeleteSheet, setShowDeleteSheet] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [longPressTimer, setLongPressTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [hiddenConversations, setHiddenConversations] = React.useState<Set<string>>(() => {
    const saved = localStorage.getItem('hidden_conversations');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Filter out hidden conversations
  React.useEffect(() => {
    const filtered = conversations.filter(c => !hiddenConversations.has(c.id));
    setLocalConversations(filtered);
  }, [conversations, hiddenConversations]);

  // Watch for triggerNewMessage from parent and open the new conversation modal
  React.useEffect(() => {
    if (triggerNewMessage) {
      setShowNewConversation(true);
      onNewMessageHandled?.();
    }
  }, [triggerNewMessage]);

  React.useEffect(() => {
    let subscription: any = null;

    const setupSubscription = async () => {
      try {
        subscription = await subscribeToConversationUpdates((updatedConversation: any) => {
          setLocalConversations(prev => {
            const filtered = prev.filter(c => c.id !== updatedConversation.id);
            const newOrder = [updatedConversation, ...filtered];
            onConversationUpdate?.(newOrder);
            return newOrder;
          });
        });
      } catch (error) {
        console.error('Error setting up conversation updates:', error);
      }
    };

    setupSubscription();
    return () => { if (subscription) subscription.unsubscribe(); };
  }, [onConversationUpdate]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setShowNewMessageMenu(false);
    if (showNewMessageMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showNewMessageMenu]);

  const searchUsers = async (query: string) => {
    if (!query.trim()) { setSearchResults([]); return; }

    setSearchLoading(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return;

      const { data, error } = await supabase
        .from('users')
        .select('id, username, avatar_url')
        .ilike('username', `%${query}%`)
        .neq('id', currentUser.id)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleStartConversation = async (user: any) => {
    setShowNewConversation(false);
    setSearchQuery('');
    setSearchResults([]);

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return;

      const { data: result, error: createError } = await supabase
        .rpc('create_or_get_direct_chat', { user1_id: currentUser.id, user2_id: user.id });

      if (createError || !result.success) return;

      onSelect({
        id: result.conversation.id,
        type: 'direct',
        otherUser: { id: user.id, username: user.username, avatar_url: user.avatar_url },
        lastMessage: result.conversation.last_message,
        lastMessageAt: result.conversation.last_message_at
      });
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  // Long press handlers for mobile
  const handleLongPressStart = (e: React.TouchEvent, conversation: Conversation) => {
    const timer = setTimeout(() => {
      setSelectedConversation(conversation);
      setShowDeleteSheet(true);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;

    setDeleting(true);
    try {
      // Save to localStorage to persist across refreshes
      const newHidden = new Set(hiddenConversations);
      newHidden.add(selectedConversation.id);
      setHiddenConversations(newHidden);
      localStorage.setItem('hidden_conversations', JSON.stringify([...newHidden]));

      // Also update local state
      setLocalConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
      onConversationUpdate?.(localConversations.filter(c => c.id !== selectedConversation.id));

      setShowDeleteSheet(false);
      setSelectedConversation(null);
    } catch (err) {
      console.error('Error deleting conversation:', err);
    } finally {
      setDeleting(false);
    }
  };

  const filteredConversations = React.useMemo(() => {
    if (!searchQuery.trim()) return localConversations;
    return localConversations.filter(conv => {
      if (conv.type === 'direct' && conv.otherUser) {
        return conv.otherUser.username.toLowerCase().includes(searchQuery.toLowerCase());
      } else if (conv.type === 'group' && conv.name) {
        return conv.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return false;
    });
  }, [localConversations, searchQuery]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) searchUsers(searchQuery);
      else setSearchResults([]);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <>
      <div className="h-full flex flex-col bg-white">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-content">Messages</h2>
            <button
              type="button"
              onClick={() => { setShowNewConversation(true); }}
              className="hidden lg:flex items-center gap-1.5 px-3 py-2 bg-accent-text text-white rounded-full text-sm font-medium hover:bg-accent-text/90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New message</span>
            </button>
          </div>

          {/* New Message Dropdown Menu */}
          {showNewMessageMenu && (
            <div className="absolute right-6 top-16 z-20 bg-white rounded-xl shadow-lg border border-gray-200 py-1 min-w-[200px]">
              <button
                onClick={() => { setShowNewConversation(true); setShowNewMessageMenu(false); }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-accent-text/10 flex items-center justify-center">
                  <Send className="h-4 w-4 text-accent-text" />
                </div>
                <div>
                  <p className="text-sm font-medium text-content">New message</p>
                  <p className="text-xs text-content/50">Start a direct conversation</p>
                </div>
              </button>
              <button
                onClick={() => { onCreateGroup(); setShowNewMessageMenu(false); }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-accent-text/10 flex items-center justify-center">
                  <Users className="h-4 w-4 text-accent-text" />
                </div>
                <div>
                  <p className="text-sm font-medium text-content">Create group</p>
                  <p className="text-xs text-content/50">Start a group conversation</p>
                </div>
              </button>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-accent-text/40 focus:ring-1 focus:ring-accent-text/10 transition-colors placeholder:text-content/30"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-content/30" />
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto px-2.5">
          {filteredConversations.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="w-12 h-12 bg-accent-text/5 rounded-xl flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="h-5 w-5 text-accent-text/30" />
              </div>
              <p className="text-sm text-content/40 mb-3">No conversations yet</p>
              <button
                onClick={() => setShowNewConversation(true)}
                className="inline-flex items-center px-3.5 py-2 text-xs font-medium text-white bg-accent-text rounded-lg hover:bg-accent-text/90 transition-colors"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Start Conversation
              </button>
            </div>
          ) : (
            <div className="space-y-0.5 pb-2">
              {filteredConversations.map((conversation) => {
                const isActive = conversation.id === activeConversationId;
                const displayName = conversation.type === 'group' ? conversation.name : conversation.otherUser?.username;
                const avatarUrl = conversation.type === 'group' ? conversation.image_url : conversation.otherUser?.avatar_url;
                const avatarId = conversation.type === 'group' ? undefined : conversation.otherUser?.id;

                return (
                  <button
                    key={conversation.id}
                    onClick={() => onSelect(conversation)}
                    onTouchStart={(e) => handleLongPressStart(e, conversation)}
                    onTouchEnd={handleLongPressEnd}
                    className={`w-full flex items-center gap-4 px-4 py-5 md:py-4 rounded-xl transition-all duration-150 text-left group ${
                      isActive
                        ? 'bg-accent-text/8 shadow-sm'
                        : 'hover:bg-white/80'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <Avatar
                        url={avatarUrl}
                        size="md"
                        userId={avatarId}
                        editable={false}
                        className="w-9 h-9 lg:w-10 lg:h-10"
                      />
                      {conversation.type === 'direct' && conversation.otherUser?.isOnline && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1 md:mb-0.5">
                        <span className={`text-base md:text-sm truncate ${isActive ? 'font-semibold text-content' : 'font-medium text-content/90'}`}>
                          {displayName}
                        </span>
                        {conversation.lastMessageAt && (
                          <span className="text-xs md:text-[11px] text-content/35 flex-shrink-0 ml-2">
                            {formatTime(conversation.lastMessageAt)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        {conversation.lastMessage && (
                          <p className="text-sm md:text-xs text-content/45 truncate leading-relaxed pr-2">
                            {conversation.lastMessage}
                          </p>
                        )}
                        {conversation.unreadCount ? (
                          <span className="flex-shrink-0 min-w-[24px] h-6 md:h-5 bg-accent-text text-white text-xs md:text-[10px] font-bold rounded-full flex items-center justify-center px-2 md:px-1.5">
                            {conversation.unreadCount}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-content">New Conversation</h3>
              <button
                onClick={() => { setShowNewConversation(false); setSearchQuery(''); setSearchResults([]); }}
                className="p-1.5 text-content/40 hover:text-content hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-4.5 w-4.5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-content/30" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by username..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-accent-text/40 focus:ring-1 focus:ring-accent-text/10 outline-none transition-colors placeholder:text-content/30"
                  autoFocus
                />
              </div>
            </div>

            {/* Results */}
            <div className="max-h-80 overflow-y-auto">
              {searchLoading ? (
                <div className="flex justify-center items-center p-8">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-text border-t-transparent"></div>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="p-8 text-center text-content/40 text-sm">
                  {searchQuery.trim() ? 'No users found' : 'Start typing to search users'}
                </div>
              ) : (
                <div className="p-2">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleStartConversation(user)}
                      className="w-full p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left rounded-xl"
                    >
                      <Avatar url={user.avatar_url} size="md" userId={user.id} editable={false} />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm text-content block truncate">{user.username}</span>
                        <span className="text-xs text-content/40">Start conversation</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Create Group Button */}
              <div className="p-4 border-t border-gray-100">
                <button
                  onClick={() => { setShowNewConversation(false); onCreateGroup?.(); }}
                  className="w-full py-3 flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl text-content/60 hover:border-accent-text hover:text-accent-text transition-colors"
                >
                  <Users className="h-4 w-4" />
                  <span className="text-sm font-medium">Create Group Chat</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Conversation Bottom Sheet */}
      <BottomSheet
        isOpen={showDeleteSheet}
        onClose={() => { setShowDeleteSheet(false); setSelectedConversation(null); }}
        title="Conversation"
      >
        <div className="px-4 space-y-4 pb-6">
          <p className="text-content/70 text-center">
            Hide this conversation? You can start a new chat or if {selectedConversation?.type === 'direct' ? 'they' : 'someone'} message(s), it will reappear with full history.
          </p>
          <button
            onClick={handleDeleteConversation}
            disabled={deleting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 disabled:opacity-50 transition-colors"
          >
            {deleting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              <>
                <Trash2 className="h-5 w-5" />
                <span>Hide Conversation</span>
              </>
            )}
          </button>
        </div>
      </BottomSheet>
    </>
  );
}
